import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../shared/components/pagination-bar/pagination-bar.component';
import { Company } from '../../services/company.service';
import { FailedLogoTracker, companyInitials } from '../../utils/company-display.util';

/** Optional columns callers can switch on; name and status always render. */
export type CompanyListColumn = 'email' | 'contact' | 'locations' | 'created';

const DEFAULT_COLUMNS: CompanyListColumn[] = ['email', 'locations'];

/**
 * Presentational company list: search toolbar, table, load/error/empty states
 * and pagination. Owns no data — the parent fetches through CompanyService.
 *
 * Rows link to `rowLink` when supplied: the whole row is clickable, and the name
 * is a real anchor so keyboard and screen-reader users get a focusable target
 * rather than a click handler they cannot reach. Per-row actions go in a kebab
 * menu whose contents come from `menuTemplate`; this component owns the trigger
 * and open/close behaviour so every caller gets the same interaction.
 */
@Component({
  selector: 'app-company-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PaginationBarComponent],
  templateUrl: './company-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./company-list.component.scss'],
})
export class CompanyListComponent {
  private readonly router = inject(Router);

  @Input() companies: Company[] = [];
  @Input() loading = false;
  @Input() errorMessage = '';
  @Input() searchTerm = '';

  /** Column header for the first cell. */
  @Input() nameHeader = 'Company';
  @Input() searchPlaceholder = 'Search companies by name, email, or address…';
  @Input() emptyMessage = 'No companies found.';
  @Input() loadingMessage = 'Loading companies…';
  @Input() columns: CompanyListColumn[] = DEFAULT_COLUMNS;

  /** Router path prefix for the row link; the company id is appended. */
  @Input() rowLinkBase: any[] | null = null;

  /** Rendered inside the open kebab menu. Omit to hide the actions column. */
  @Input() menuTemplate: TemplateRef<{ $implicit: Company }> | null = null;

  @Input() showRefresh = true;

  @Input() paginationData: PaginationData = {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 20,
  };
  @Input() itemsPerPageOptions: number[] = [10, 20, 50];

  @Output() searchTermChange = new EventEmitter<string>();
  @Output() refresh = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() itemsPerPageChange = new EventEmitter<number>();

  openMenuCompanyId: string | null = null;
  menuPanelTop = 0;
  menuPanelRight = 0;

  private readonly failedLogos = new FailedLogoTracker();

  readonly companyInitials = companyInitials;

  has(column: CompanyListColumn): boolean {
    return this.columns.includes(column);
  }

  get rowsAreLinked(): boolean {
    return !!this.rowLinkBase;
  }

  rowLink(company: Company): any[] {
    return [...(this.rowLinkBase || []), company.id];
  }

  /**
   * Whole-row navigation. Clicks that land on the name anchor or a menu control
   * are left alone — those elements navigate or open the menu themselves.
   */
  onRowClick(company: Company, event: Event): void {
    if (!this.rowsAreLinked) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button')) {
      return;
    }
    void this.router.navigate(this.rowLink(company));
  }

  toggleMenu(company: Company, event: Event): void {
    // Keep the row link from firing when the trigger is inside a linked row.
    event.stopPropagation();
    event.preventDefault();
    if (this.openMenuCompanyId === company.id) {
      this.closeMenu();
      return;
    }

    const trigger = event.currentTarget as HTMLElement | null;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    this.menuPanelTop = rect.bottom + 4;
    this.menuPanelRight = window.innerWidth - rect.right;
    this.openMenuCompanyId = company.id;
  }

  closeMenu(): void {
    this.openMenuCompanyId = null;
  }

  /** Menu items sit inside a linked row, so their clicks must not navigate it. */
  onMenuClick(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMenu();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    this.closeMenu();
  }

  logoUrl(company: Company): string | null {
    return this.failedLogos.url(company);
  }

  onLogoError(company: Company): void {
    this.failedLogos.markFailed(company);
  }

  locationsLabel(company: Company): string {
    const count = company.no_of_locations;
    if (count === null || count === undefined) {
      return '—';
    }
    return `${count} location${count === 1 ? '' : 's'}`;
  }

  formatDate(value?: string): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  }

  trackById(_: number, company: Company): string {
    return company.id;
  }
}
